import { apiClient, apiFileUploadClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { ToastType } from '@components/Toast';
import { LTLCatalogs, V4CatalogApiResponse } from '@store/orders';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { AddProductDetails } from '@typeDefinitions/customerTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';

export async function fetchProductCatalog(customerId: number) {
  try {
    const resp: V4CatalogApiResponse<LTLCatalogs[]> = await apiClient.post(
      `/LtlCatalog/getltlcatalogs/${customerId}`,
    );

    return resp;
  } catch (error: unknown) {
    logError('fetchProductCatalog', error as Error);
    throw createError(error, 'Failed to fetch product catalog');
  }
}

export const addProductDetails = async (
  payload: AddProductDetails,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response = await apiClient.post(
      'LtlCatalog',
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return response as unknown as AddProductDetails;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to Add Product Details');
    handleToast(ToastType.Error, e.message);
  }
};

export const editProductDetails = async (
  payload: AddProductDetails,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response = await apiClient.put(
      'LtlCatalog',
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return response as unknown as AddProductDetails;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to Edit Product Details');
    handleToast(ToastType.Error, e.message);
  }
};

export const deleteProductDetails = async (
  id: string,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res: {
      isSuccess: boolean;
    } = await apiClient.delete(
      `LtlCatalog/${id}`,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    if (Boolean(res.isSuccess)) {
      handleToast(ToastType.Success, 'Product Deleted Successfully');
      return res;
    }
    handleToast(ToastType.Error, 'Failed to delete product');
  } catch (error: unknown) {
    throw createError(error, 'Failed to Delete Product Details');
  }
};

export const fetchProductById = async (id: string) => {
  try {
    const response = await apiClient.get(`LtlCatalog/${id}`);
    return response as unknown as AddProductDetails;
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to fetch product by id');
    throw createError(error, 'Failed to fetch product by id');
  }
};

export const uploadProductCatalog = async (
  id: string,
  file: File,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const formData = new FormData();
    formData.append('File', file);
    formData.append('CustomerId', id.toString());

    const res: V4ApiResponse<{
      failedCount: number;
      failedCsv: string;
    }> = await apiFileUploadClient.post(
      '/LTLCatalog/ProductImport',
      formData,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return res;
  } catch (error) {
    throw createError(error, 'Failed to upload product catalog');
  }
};
